import 'src/styles/ui/button.css';

import classNames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';

/**
 * Reusable pill action button — the app's outlined action-button style
 * (rounded pill, colored 1.5px outline, tinted hover). Variants render as
 * outlines; pass `fill` for the solid emphasis CTA.
 *
 * @example
 * <Button variant="primary" icon={<FontAwesomeIcon icon={faQrcode} />}>
 *   New Scan
 * </Button>
 *
 * @example
 * <Button fill variant="success" block onClick={handleStart}>
 *   Start work
 * </Button>
 */
const Button = ({
  variant = 'default',
  size = 'medium',
  fill = false,
  block = false,
  disabled = false,
  loading = false,
  icon = null,
  iconPosition = 'left',
  badge = null,
  className = '',
  children,
  ...props
}) => {
  const classes = classNames(
    'button',
    `button--${variant}`,
    {
      'button--fill': fill,
      'button--sm': size === 'small',
      'button--lg': size === 'large',
      'button--full': block,
    },
    className
  );

  // While loading, swap the icon for the app's spinner and lock the button.
  const leadingIcon = loading ? (
    <span className='spinner-border spinner-border-sm' role='status' aria-hidden='true' />
  ) : (
    icon
  );

  return (
    <button type='button' className={classes} disabled={disabled || loading} {...props}>
      {leadingIcon && iconPosition === 'left' && (
        <span className='button__icon'>{leadingIcon}</span>
      )}
      {children}
      {badge != null && <span className='button__badge'>{badge}</span>}
      {leadingIcon && iconPosition === 'right' && (
        <span className='button__icon'>{leadingIcon}</span>
      )}
    </button>
  );
};

Button.propTypes = {
  variant: PropTypes.oneOf([
    'primary',
    'secondary',
    'info',
    'link',
    'default',
    'success',
    'danger',
  ]),
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  fill: PropTypes.bool,
  block: PropTypes.bool,
  disabled: PropTypes.bool,
  loading: PropTypes.bool,
  icon: PropTypes.node,
  iconPosition: PropTypes.oneOf(['left', 'right']),
  badge: PropTypes.node,
  className: PropTypes.string,
  children: PropTypes.node,
};

export default Button;
